import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  CheckCircle2, Clock, ShieldCheck, LogOut, Sparkles, Lock,
  Pencil, Tag, MapPin, Building2, Globe, Phone, Send, MessageCircle, Heart,
} from "@/lib/icons";
import IdCard from "@/components/IdCard";
import PhotoUpload from "@/components/PhotoUpload";
import ShareBar, { memberShareTitle } from "@/components/ShareBar";
import AccountSecurity from "@/components/AccountSecurity";
import EditProfilePanel from "@/components/EditProfilePanel";
import InvoiceDashboard from "@/components/InvoiceDashboard";
import { CategoryChips, selectedCategories } from "@/components/CategoryMultiSelect";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";

const REQUIRED_FIELDS = [
  { key: "category", label: "Category" },
  { key: "state", label: "State" },
  { key: "city", label: "City" },
  { key: "blood_group", label: "Blood group" },
];

export default function MemberAccount() {
  const { user, logout, setUser } = useAuth();
  const [activating, setActivating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  useSeo({ title: "My Account | PRONET", noindex: true });
  if (user === null) return <div className="container-tight py-20 text-white/50">Loading…</div>;
  if (!user || user.role !== "member") return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/account/first-login" replace />;

  const s = user.status || "pending";
  const isApproved = s === "approved";
  const isActivated = !!user.pro_card_activated;
  const cardUnlocked = !!user.card_shared_at;
  // Points at the server-rendered share shim (real Open Graph tags + an
  // instant redirect), not the SPA route directly — WhatsApp/Facebook/
  // Twitter's preview crawlers never run client JS, so sharing the SPA URL
  // itself would show no image and generic text for every member.
  const profileUrl = `${window.location.origin}/api/public/members/${encodeURIComponent(user.member_id)}/share`;
  const missingFields = REQUIRED_FIELDS.filter((f) => !(user[f.key] || "").toString().trim());

  const activateProCard = async () => {
    setActivating(true);
    try {
      const { data } = await api.post("/member/pro-card/activate");
      if (data?.ok) {
        setUser({ ...user, pro_card_activated: true });
        toast.success("Pro Card activated");
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setActivating(false);
    }
  };

  const handleCardUnlock = async () => {
    if (user.card_shared_at) return;
    try {
      const { data } = await api.post("/member/card/unlock");
      setUser({ ...user, card_shared_at: data.card_shared_at });
      toast.success("Card unlocked. Thanks for sharing!");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div className="relative container-tight py-14 md:py-20">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[500px] h-[500px] -top-40 -left-20 opacity-30" />
      </div>
      <div className="relative z-10 fade-up space-y-6">
        <div className="overline text-fuchsia-300">My PRONET</div>
        <h1 className="mt-1 font-display text-5xl md:text-6xl text-white leading-none">
          Hi, <span className="italic text-gradient">{user.full_name?.split(" ")[0] || "Photographer"}</span>
        </h1>

        {/* PROFILE HEADER */}
        <div className="glass rounded-3xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 sm:items-center" data-testid="account-header">
          <PhotoUpload
            src={user.photo_url}
            endpoint="/member/photo"
            deleteEndpoint="/member/photo"
            testidPrefix="account-photo"
            size="md"
            name={user.full_name}
            avatarKey={user.avatar_key}
            onChange={(url) => setUser({ ...user, photo_url: url })}
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <div className="font-display text-2xl text-white truncate">{user.full_name}</div>
              {isApproved ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-3 py-1 text-xs font-semibold text-emerald-300">
                  <CheckCircle2 size={12} /> Pro ID
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/30 px-3 py-1 text-xs font-semibold text-amber-300">
                  <Clock size={12} /> Pending verification
                </span>
              )}
            </div>
            {user.studio_name && <div className="mt-1 text-sm text-white/60">{user.studio_name}</div>}
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/60">
              <span><span className="text-white/40">Member ID</span> <span className="font-mono text-white/80">{user.member_id}</span></span>
              <span><span className="text-white/40">Mobile</span> <span className="tabular-nums text-white/80">{user.mobile}</span></span>
              {user.email && <span><span className="text-white/40">Email</span> <span className="text-white/80">{user.email}</span></span>}
            </div>
          </div>
          <div className="flex sm:flex-col gap-2 shrink-0">
            <button onClick={() => setEditOpen(true)} data-testid="acc-edit-profile"
              className="btn-glow inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white">
              <Pencil size={13} /> Edit Profile
            </button>
            {isApproved && (
              <Link to={`/member/${user.member_id}`} data-testid="acc-view-profile"
                className="btn-ghost inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white">
                <ShieldCheck size={13} className="text-emerald-300" /> View public profile
              </Link>
            )}
            <button onClick={logout} data-testid="acc-logout"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-red-300 border border-red-500/20 hover:bg-red-500/10 transition-colors">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>

        {/* INVOICING */}
        <InvoiceDashboard />

        {/* PROFILE DETAILS */}
        <div className="glass rounded-3xl p-6 md:p-8" data-testid="account-details">
          <div className="flex items-center justify-between">
            <div className="overline text-white/50">Profile details</div>
            <button onClick={() => setEditOpen(true)} data-testid="acc-edit-profile-secondary"
              className="text-xs font-semibold text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center gap-1">
              <Pencil size={11} /> Edit
            </button>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
            <div className="sm:col-span-2 lg:col-span-3 rounded-2xl border border-white/10 bg-white/[0.025] p-4" data-testid="account-categories">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="overline text-white/50 text-[10px] flex items-center gap-1.5">
                  <Tag size={12} className="text-fuchsia-300" /> Selected categories
                </div>
                <button type="button" onClick={() => setEditOpen(true)} data-testid="account-edit-categories"
                  className="rounded-xl border border-violet-400/30 bg-violet-500/10 px-3 py-2 text-xs font-semibold text-violet-200 hover:bg-violet-500/15">
                  Select multiple categories
                </button>
              </div>
              <CategoryChips categories={selectedCategories(user)} primary={user.category} className="mt-3" />
            </div>
            <DetailField icon={Building2} label="Studio / business" value={user.studio_name} />
            <DetailField icon={MapPin} label="State" value={user.state} required missing={fieldMissing(user, "state")} />
            <DetailField icon={MapPin} label="City" value={user.city} required missing={fieldMissing(user, "city")} />
            <DetailField icon={MapPin} label="Area" value={user.area} />
            <DetailField icon={MapPin} label="Pincode" value={user.pincode} />
            <DetailField icon={Heart} label="Blood group" value={user.blood_group} required missing={fieldMissing(user, "blood_group")} />
            <DetailField icon={Globe} label="Website" value={user.website} />
            <DetailField icon={MessageCircle} label="WhatsApp" value={user.whatsapp_number} />
            <DetailField icon={Send} label="Telegram" value={user.telegram_username} />
            <DetailField icon={Phone} label="Preferred contact" value={PREFERRED_LABELS[user.preferred_contact_method] || "Any method"} />
          </div>
          {user.bio && (
            <div className="mt-5 pt-5 border-t border-white/10">
              <div className="overline text-white/40 text-[10px]">Bio</div>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{user.bio}</p>
            </div>
          )}
        </div>

        {/* PRO CARD */}
        <div className="glass rounded-3xl p-6 md:p-8 relative overflow-hidden" data-testid="account-id-card-section">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-gradient-to-br from-violet-500/20 to-magenta-500/20 blur-3xl" />
          <div className="relative">
            <div className="overline text-fuchsia-300">Your Pro Card</div>
            <div className="mt-2 font-display text-3xl text-white">Your PRONET Pro ID</div>

            {!isApproved && (
              <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-400/25 bg-amber-500/[0.06] p-4" data-testid="procard-locked">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/30 shrink-0">
                  <Lock size={16} />
                </span>
                <div className="text-sm text-amber-100/85">
                  Your profile must be approved before you can access the Pro Card.
                  <div className="text-xs text-amber-100/60 mt-1">Our admin team usually reviews profiles within 24 to 48 hours.</div>
                </div>
              </div>
            )}

            {isApproved && missingFields.length > 0 && (
              <div className="mt-6 relative mx-auto max-w-[432px] overflow-hidden rounded-3xl" data-testid="procard-incomplete">
                <div className="pointer-events-none select-none blur-lg opacity-60" aria-hidden="true">
                  <IdCard member={user} showActions={false} />
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-3xl bg-gradient-to-b from-[#0b0612]/60 via-[#0b0612]/85 to-[#0b0612]/95 p-6 text-center">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/30">
                    <Lock size={18} />
                  </span>
                  <div>
                    <div className="font-display text-2xl text-white">Fill your profile to reveal your card</div>
                    <p className="mt-2 text-sm text-amber-100/70 max-w-xs mx-auto">
                      Missing: {missingFields.map((f) => f.label).join(", ")}.
                    </p>
                  </div>
                  <button onClick={() => setEditOpen(true)} data-testid="procard-complete-profile"
                    className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold text-white">
                    <Pencil size={12} /> Complete profile
                  </button>
                </div>
              </div>
            )}

            {isApproved && missingFields.length === 0 && !isActivated && (
              <>
                <p className="mt-2 text-sm text-white/60 max-w-xl">
                  Activate your Pro Card to generate a shareable, downloadable PRONET credential with a QR that verifies your profile online.
                </p>
                <button
                  onClick={activateProCard}
                  disabled={activating}
                  data-testid="procard-activate-btn"
                  className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-3 mt-6 text-sm font-semibold text-white disabled:opacity-60"
                >
                  <Sparkles size={14} /> {activating ? "Activating…" : "Share & Download Pro Card"}
                </button>
              </>
            )}

            {isApproved && missingFields.length === 0 && isActivated && !cardUnlocked && (
              <>
                <p className="mt-2 text-sm text-white/60 max-w-xl">
                  Share your profile once to unlock your digital card. You can then view, download or print it.
                </p>
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-fuchsia-400/25 bg-fuchsia-500/[0.06] p-4" data-testid="procard-share-gate">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-400/30 shrink-0">
                    <Lock size={16} />
                  </span>
                  <div className="text-sm text-fuchsia-100/85 flex-1">
                    Your card is ready. Share your profile link once to reveal it.
                  </div>
                </div>
                <div className="mt-6">
                  <ShareBar
                    url={profileUrl}
                    title={memberShareTitle(user.full_name)}
                    subtitle={user.studio_name || user.category || ""}
                    testidPrefix="account-share-gate"
                    onShared={handleCardUnlock}
                  />
                </div>
              </>
            )}

            {isApproved && missingFields.length === 0 && isActivated && cardUnlocked && (
              <>
                <p className="mt-2 text-sm text-white/60 max-w-xl">
                  Share the QR code, download the PDF or print your PRONET Pro ID.
                </p>
                <div className="mt-6" data-testid="procard-card"><IdCard member={user} showActions={true} /></div>
                <div className="mt-6">
                  <ShareBar
                    url={profileUrl}
                    title={memberShareTitle(user.full_name)}
                    subtitle={user.studio_name || user.category || ""}
                    testidPrefix="account-share"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Security: change password + change email */}
        <AccountSecurity user={user} onUserUpdated={setUser} />
      </div>

      <EditProfilePanel
        open={editOpen}
        onOpenChange={setEditOpen}
        user={user}
        onSaved={(updated) => setUser({ ...user, ...updated })}
      />
    </div>
  );
}

const PREFERRED_LABELS = { phone: "Phone call", whatsapp: "WhatsApp", telegram: "Telegram", any: "Any method" };

function fieldMissing(user, key) {
  return !(user[key] || "").toString().trim();
}

function DetailField({ icon: Icon, label, value, required, missing }) {
  return (
    <div>
      <div className="overline text-white/40 text-[10px] flex items-center gap-1.5">
        <Icon size={11} className="text-fuchsia-300/70" /> {label} {required && <span className="text-fuchsia-400">*</span>}
      </div>
      {missing ? (
        <div className="mt-1.5 text-sm text-amber-300/80 italic">Not set</div>
      ) : (
        <div className="mt-1.5 text-sm text-white/85">{value}</div>
      )}
    </div>
  );
}
